import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { CredentialsService } from '@app/@core/services/credentials.service';

const routes = {
  locations: () => '/org/merchant/company/locations',
  getCityIdByLatLong: (lat: string, lng: string) =>
    `/org/delivery/Location/GetCityIdByLatLong?latitude=${lat}&longitude=${lng}`,
  getCustomerAddressByPhone: (phone: string) =>
    `/org/delivery/Order/findCustomerAddressByPhone?phone=${phone}`,
  addressVerification: () => `/org/sgs/address/verification`,
};

/**
 * The customer/address data shape flowing through the ahoy-customer module.
 * Field casing (camelCase) matches the merchant-facing API responses (e.g.
 * findCustomerAddressByPhone) once run through capitalizeCustomerProperties.
 * All fields are optional and there's deliberately no index signature: callers
 * elsewhere in the app (e.g. the profile/address-book module's own
 * `AddressBookCustomerData`) pass their own differently-declared but
 * structurally compatible objects into `CustomerDetailsComponent.bindCustomer`.
 */
export interface CustomerDetailsData {
  customerName?: string;
  addressName?: string;
  customerPhone?: string;
  customerEmail?: string;
  nationalAddress?: string;
  dontContactCustomer?: boolean;
  customerAddress?: string;
  customerAddressTypeId?: number;
  customerLatitude?: string | number;
  customerLongitude?: string | number;
  customerLocationLatitude?: string | number;
  customerLocationLongitude?: string | number;
  area?: string;
  building?: string;
  floor?: string;
  unit?: string;
  customerAddressNote?: string;
  tagLocationName?: string;
}

export interface CustomerDetailsConfigurations {
  callFindCustomerAddressByPhone: boolean;
  defaultPhoneCode: string;
  hideCustomerName: boolean;
  hideContactCustomer: boolean;
  hideAddressName: boolean;
  hideTagLocationName: boolean;
  validatePlaces: boolean;
  disablePhoneField: boolean;
  initialLatitude: number;
  initialLongitude: number;
  validateAddress: boolean;
  departureAirportId: number;
  arrivalAirportId: number;
  phoneNumber: string;
}

export interface AddressVerificationQuery {
  latitude: number;
  longitude: number;
  departureAirportId?: number;
  arrivalAirportId?: number;
  [key: string]: number | undefined;
}

export interface AddressVerificationResult {
  isValid: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AhoyCustomerService {
  constructor(
    private httpClient: HttpClient,
    private credentialsService: CredentialsService,
  ) {}

  getLocations(): Observable<string> {
    return this.httpClient.get(routes.locations()).pipe(
      map((body: unknown) => body as string),
      // catchError(() => of('Couldn\'t load locations :('))
    );
  }

  getCityIdByLatLong(lat: string, lng: string): Observable<unknown> {
    return this.httpClient.get(routes.getCityIdByLatLong(lat, lng), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService?.credentials?.token}`,
      }),
    });
  }

  addressVerification(
    context: AddressVerificationQuery,
    guestToken?: string,
  ): Observable<AddressVerificationResult> {
    let params = new HttpParams();
    for (let key in context) {
      if (context.hasOwnProperty(key)) {
        params = params.set(key, context[key] as number);
      }
    }
    const headers = guestToken
      ? new HttpHeaders({ 'X-OTP-Token': guestToken })
      : new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService?.credentials?.token}`,
        });
    return this.httpClient.get<AddressVerificationResult>(
      routes.addressVerification(),
      {
        headers,
        params,
      },
    );
  }

  getCustomerInformationByPhone(
    phone: string,
  ): Observable<CustomerDetailsData> {
    return this.httpClient
      .get<CustomerDetailsData>(routes.getCustomerAddressByPhone(phone), {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService?.credentials?.token}`,
        }),
      })
      .pipe(
        map((res) => {
          if (!res) {
            throw throwError('Customer not found');
          }
          return res;
        }),
      );
  }

  capitalizeCustomerProperties(
    customer: Record<string, unknown>,
    camelInd: boolean,
  ): Record<string, unknown> {
    for (let prop in customer) {
      let newPropertyName = '';
      if (camelInd) {
        newPropertyName = this.camelCase(prop)!;
        customer[newPropertyName] = customer[prop];
      } else {
        newPropertyName = this.capitalize(prop)!;
        customer[newPropertyName] = customer[prop];
      }
      // if the property was already in the correct case it shouldn't be deleted
      if (prop !== newPropertyName) {
        delete customer[prop];
      }
    }
    return customer;
  }

  private camelCase(str: string) {
    if (str.length) {
      return `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
    }
  }

  private capitalize(str: string) {
    if (str.length) {
      return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
    }
  }
}
